import { CancelledError, useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { match } from 'ts-pattern';

import { FEATURE } from '~/common/enums/feature.enum';
import type { PrefetchConfig } from '~/common/hooks/usePrefetch';
import { UserContext } from '~/components/providers/UserProvider';
import type { League } from '~/domains/commissioner/interfaces/league.interface';
import { useLeagues } from '~/domains/common/hooks/useLeagues';
import { useSettledInView } from '~/domains/common/hooks/useSettledInView';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { useLocationKey } from '~/hooks/useLocationKey';
import type {
  PromotedContestCarousel,
  PromotedContestTab,
} from '~/services/marketing/promoted-contests.schema';

import { CondensedCarouselsQueryKeys, type HomeCollectionSlug } from '../../constants/query';
import { SETTLE_IN_VIEW_MS } from '../../constants/rail';
import { useRailContests, type UseRailContestsReturn } from '../../hooks/useRailContests';
import { useRailFullyVisibleCount } from '../../hooks/useRailFullyVisibleCount';
import { useRailNextBatchTrigger } from '../../hooks/useRailNextBatchTrigger';
import { resolvePillSelection } from '../../utils/pillSelection';
import { getFirstBatchSize } from '../../utils/railBatches';
import { createRailQueryOptions } from '../../utils/railQueryOptions';
import { buildViewMoreLink } from '../../utils/viewMoreLink';

type UseCondensedCarouselModuleParams = {
  slug: HomeCollectionSlug;
  carousel: PromotedContestCarousel;
};

export type CarouselDisplayState = 'hidden' | 'pending' | 'ready';

export type UseCondensedCarouselModuleReturn = {
  elementRef: RefObject<HTMLElement>;
  displayState: CarouselDisplayState;
  showPills: boolean;
  visibleTabs: PromotedContestTab[];
  activeTab: PromotedContestTab;
  selectTab: (value: string) => void;
  rail: UseRailContestsReturn;
  scrollerRef: RefObject<HTMLDivElement>;
  trackRef: (node: HTMLElement | null) => void;
  sentinelRef: (node: HTMLElement | null) => void;
  viewMore: ReturnType<typeof buildViewMoreLink>;
  getPrefetchConfig: (tab: PromotedContestTab) => PrefetchConfig | undefined;
  promoSource: string;
  railId: string;
  isContestThumbnailShown: boolean;
  isCountdownPillEnabled: boolean;
};

const UNMEASURED_BATCH_SIZE = 0;

const NO_KNOWN_EMPTY_KEYS: ReadonlySet<string> = new Set();

const NO_LEAGUES: League[] = [];

const toKnownEmptyKey = ({ value, contest_ids }: PromotedContestTab) =>
  JSON.stringify([value, contest_ids]);

const toDisplayState = (isHidden: boolean, hasSettledInView: boolean) =>
  match({ isHidden, hasSettledInView })
    .returnType<CarouselDisplayState>()
    .with({ isHidden: true }, () => 'hidden')
    .with({ hasSettledInView: false }, () => 'pending')
    .with({ hasSettledInView: true }, () => 'ready')
    .exhaustive();

const toFirstBatchSize = (fullyVisibleCount: number | null, tab: PromotedContestTab) =>
  fullyVisibleCount === null
    ? UNMEASURED_BATCH_SIZE
    : getFirstBatchSize(fullyVisibleCount, tab.contest_ids.length);

export const useCondensedCarouselModule = ({
  slug,
  carousel,
}: UseCondensedCarouselModuleParams): UseCondensedCarouselModuleReturn => {
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);
  const { locationKey, isLocationReady } = useLocationKey();
  const isContestThumbnailShown = useIsFeatureEnabled(FEATURE.SHOW_CONTEST_ITEM_THUMBNAIL);
  const isCountdownPillEnabled = useIsFeatureEnabled(FEATURE.ENABLE_CONTEST_COUNTDOWN_TIMER);

  const { elementRef, isInView, hasSettledInView } = useSettledInView<HTMLElement>({
    settleMs: SETTLE_IN_VIEW_MS,
  });
  const { data: leagues = NO_LEAGUES } = useLeagues({ enabled: hasSettledInView });
  const isLoadingAllowed = hasSettledInView && isInView;
  const hasLeftView = hasSettledInView && !isInView;
  const isLoadingAllowedRef = useRef(isLoadingAllowed);

  useLayoutEffect(() => {
    isLoadingAllowedRef.current = isLoadingAllowed;
  });

  const [initialTabValue] = useState(() => carousel.tabs[0].value);
  const [selectedValue, setSelectedValue] = useState(initialTabValue);
  const [knownEmptyKeys, setKnownEmptyKeys] = useState(NO_KNOWN_EMPTY_KEYS);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { trackRef, fullyVisibleCount } = useRailFullyVisibleCount(scrollerRef);

  const initialTab =
    carousel.tabs.find(({ value }) => value === initialTabValue) ?? carousel.tabs[0];

  const knownEmptyValues = useMemo(
    () =>
      new Set(
        carousel.tabs
          .filter((tab) => knownEmptyKeys.has(toKnownEmptyKey(tab)))
          .map(({ value }) => value)
      ),
    [carousel.tabs, knownEmptyKeys]
  );

  const { visibleTabs, activeTab } = useMemo(
    () => resolvePillSelection({ tabs: carousel.tabs, selectedValue, knownEmptyValues }),
    [carousel.tabs, selectedValue, knownEmptyValues]
  );
  const activeValue = activeTab?.value;
  const activeKnownEmptyKey = activeTab ? toKnownEmptyKey(activeTab) : undefined;
  const railTab = activeTab ?? initialTab;
  const isInitialTabActive = railTab.value === initialTabValue;
  const railFirstBatchSize = toFirstBatchSize(fullyVisibleCount, railTab);

  const rail = useRailContests({
    slug,
    carousel,
    tab: railTab,
    firstBatchSize: railFirstBatchSize,
    fillTarget: railFirstBatchSize,
    isEnabled: isLoadingAllowed && activeTab !== null,
  });

  const hasInitialTabFailed = rail.isEmptyAfterError && isInitialTabActive;
  const hasOtherTabFailed = rail.isEmptyAfterError && !isInitialTabActive;
  const isActiveTabKnownEmpty = rail.isKnownEmpty || hasOtherTabFailed;
  const isHidden = activeTab === null || hasInitialTabFailed;
  useLayoutEffect(() => {
    if (!isActiveTabKnownEmpty || activeKnownEmptyKey === undefined) return;

    setKnownEmptyKeys((keys) => new Set(keys).add(activeKnownEmptyKey));
  }, [isActiveTabKnownEmpty, activeKnownEmptyKey]);

  useEffect(() => {
    if (!hasLeftView) return;

    queryClient.cancelQueries({
      queryKey: CondensedCarouselsQueryKeys.CAROUSEL({ slug, carouselId: carousel.id }),
    });
  }, [hasLeftView, queryClient, slug, carousel.id]);

  const sentinelRef = useRailNextBatchTrigger({
    rootRef: scrollerRef,
    onReach: rail.loadMore,
    isEnabled: rail.canFetchNextBatch,
  });

  const userId = user?.id ?? null;

  const prefetchConfigs = useMemo(
    () =>
      new Map(
        carousel.tabs.map((tab) => {
          const { queryKey, queryFn, staleTime } = createRailQueryOptions({
            slug,
            carousel,
            tab,
            userId,
            locationKey,
            firstBatchSize: toFirstBatchSize(fullyVisibleCount, tab),
          });
          const config: PrefetchConfig = {
            queryKey,
            queryFn: (context) =>
              isLoadingAllowedRef.current
                ? queryFn(context)
                : Promise.reject(new CancelledError({ revert: true })),
            staleTime,
            isInfinite: true,
          };
          return [tab.value, config] as const;
        })
      ),
    [slug, carousel, userId, locationKey, fullyVisibleCount]
  );

  const canPrefetch = isLoadingAllowed && isLocationReady && fullyVisibleCount !== null;

  const getPrefetchConfig = useCallback(
    (tab: PromotedContestTab) => {
      const config = prefetchConfigs.get(tab.value);
      if (!canPrefetch || tab.value === activeValue || config === undefined) return undefined;

      const cachedState = queryClient.getQueryState(config.queryKey);
      const hasCachedOutcome = cachedState?.data !== undefined || cachedState?.status === 'error';

      return hasCachedOutcome ? undefined : config;
    },
    [prefetchConfigs, canPrefetch, activeValue, queryClient]
  );

  const selectTab = useCallback((value: string) => setSelectedValue(value), []);

  return {
    elementRef,
    displayState: toDisplayState(isHidden, hasSettledInView),
    showPills: carousel.filterType !== null,
    visibleTabs,
    activeTab: railTab,
    selectTab,
    rail,
    scrollerRef,
    trackRef,
    sentinelRef,
    viewMore: buildViewMoreLink({
      filterType: carousel.filterType,
      viewMore: carousel.viewMore,
      activeTab: railTab,
      leagues,
    }),
    getPrefetchConfig,
    promoSource: `condensed_${slug}_${carousel.title}`,
    railId: `condensed-carousel-rail-${slug}-${carousel.id}`,
    isContestThumbnailShown,
    isCountdownPillEnabled,
  };
};
